import { useQuoteDocumentEditable, useQuoteDocumentMode } from '@/components/quote-document/QuoteDocument';
import { SheetContinuationHeaders } from '@/components/quote-document/SheetLayoutContext';
import { useCallback } from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { nanoid } from 'nanoid';
import { Plus, Trash2 } from 'lucide-react';
import { uboTheme, themeColors } from '@/theme/uboTheme';
import PageHeader from '@/components/blocks/PageHeader';
import {
  useQuoteClientLogo,
  useQuotePageBrand,
  useResolvedAgencyLogoUrl,
} from '@/store/quoteClientLogoContext';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import type { SavedInvestmentTable } from '@/lib/investmentTableLibrary';
import { propagateClientLogo } from './logoSync';
import {
  getInvestmentTableLabels,
  normalizeInvestmentLabelLanguage,
} from '@/editor/investmentTableLabels';
import { buildNewPhaseNodes, buildPhaseFromLibrary } from '@/editor/investmentPhaseActions';
import InvestmentElementBar from './InvestmentElementBar';
import type { InvestmentRateCard } from '@/types';

export default function InvestmentGroupView({
  node,
  editor,
  updateAttributes,
  getPos,
}: NodeViewProps) {
  const isEditable = useQuoteDocumentMode() === 'edit';
  const canEditText = useQuoteDocumentEditable();
  const attrs = node.attrs as Record<string, unknown>;
  const clientLogoUrl = attrs.clientLogoUrl as string | undefined;
  const quoteClientLogo = useQuoteClientLogo();
  const effectiveLogo = clientLogoUrl ?? quoteClientLogo;
  const pageBrand = useQuotePageBrand();
  const themeAttr = attrs.theme === 'ubx' || attrs.theme === 'ubo' ? attrs.theme : undefined;
  const effectiveBrand = themeAttr ?? pageBrand;
  const agencyLogoUrl = useResolvedAgencyLogoUrl(
    attrs.agencyLogoUrl as string | undefined,
    themeAttr
  );
  const insertAtEnd = useCallback(
    (nodes: any[]) => {
      if (!editor || typeof getPos !== 'function') return;
      const pos = getPos();
      if (pos == null) return;
      const insertPos = pos + node.nodeSize - 1;
      editor.chain().focus().insertContentAt(insertPos, nodes).run();
    },
    [editor, node, getPos]
  );

  const invCurrency = (attrs.currency as string) || 'EUR';
  const labelLang = normalizeInvestmentLabelLanguage(attrs.labelLanguage);
  const tableLabelsPreset = getInvestmentTableLabels(labelLang);

  const handleAddOverview = useCallback(() => {
    let overviewCount = 0;
    node.forEach((child) => {
      if (child.type.name === 'investmentHeaderNode' && !child.attrs.isMainHeader) {
        overviewCount++;
      }
    });
    insertAtEnd(buildNewPhaseNodes(overviewCount + 1, tableLabelsPreset));
  }, [node, insertAtEnd, tableLabelsPreset]);

  const handleInsertFromLibrary = useCallback(
    (saved: SavedInvestmentTable) => {
      insertAtEnd(buildPhaseFromLibrary(saved, invCurrency, tableLabelsPreset));
    },
    [insertAtEnd, invCurrency, tableLabelsPreset]
  );

  const rateCardTitleColor = themeColors(effectiveBrand === 'ubx' ? 'ubx' : 'ubo').primary;
  const rateColumnLabel = labelLang === 'en' ? 'Rate' : 'Tarief';
  const rateCard = (node.attrs.rateCard as InvestmentRateCard | null) ?? null;
  const rateCardTitle = rateCard?.title ?? '';
  const rateCardRows = rateCard?.rows ?? [];

  const setRateCard = useCallback(
    (next: InvestmentRateCard | null) => {
      updateAttributes({
        rateCard:
          next && (next.rows?.length || next.title)
            ? { title: next.title, rows: next.rows ?? [] }
            : null,
      });
    },
    [updateAttributes]
  );

  const addRateRow = useCallback(() => {
    setRateCard({
      title: rateCardTitle,
      rows: [...rateCardRows, { id: nanoid(8), label: '', rateText: '' }],
    });
  }, [rateCardTitle, rateCardRows, setRateCard]);

  const updateRateRow = useCallback(
    (id: string, patch: Partial<{ label: string; rateText: string }>) => {
      setRateCard({
        title: rateCardTitle,
        rows: rateCardRows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
      });
    },
    [rateCardTitle, rateCardRows, setRateCard]
  );

  const startRateCard = useCallback(() => {
    setRateCard({ title: '', rows: [{ id: nanoid(8), label: '', rateText: '' }] });
  }, [setRateCard]);

  const removeRateCard = useCallback(() => {
    updateAttributes({ rateCard: null });
  }, [updateAttributes]);

  const removeRateRow = useCallback(
    (id: string) => {
      const nextRows = rateCardRows.filter((r) => r.id !== id);
      setRateCard(
        nextRows.length || rateCardTitle ? { title: rateCardTitle, rows: nextRows } : null
      );
    },
    [rateCardTitle, rateCardRows, setRateCard]
  );

  return (
    <NodeViewWrapper
      as="div"
      className="investment-group-view"
      data-block-id={node.attrs.id}
      data-page-brand={effectiveBrand}
      style={{ backgroundColor: '#ffffff' }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          color: uboTheme.colors.secondary,
          padding: A4_LANDSCAPE.padding,
          minHeight: A4_LANDSCAPE.contentHeight,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        <div contentEditable={false} style={{ userSelect: 'none' }} data-sheet-skip>
          <PageHeader
            clientLogoUrl={effectiveLogo}
            agencyLogoUrl={agencyLogoUrl}
            brand={effectiveBrand}
            variant="light"
            isEditable={isEditable}
            onClientLogoChange={(url) => {
              updateAttributes({ clientLogoUrl: url });
              propagateClientLogo(editor, node, url);
            }}
          />
        </div>

        <SheetContinuationHeaders
          blockId={String(node.attrs.id)}
          clientLogoUrl={effectiveLogo}
          agencyLogoUrl={agencyLogoUrl}
          brand={effectiveBrand}
          variant="light"
        />

        <NodeViewContent style={{ marginTop: 24 }} data-sheet-body />

        {/* Alleen-lezen zonder tarieventabel is deze balk lege ruimte onderaan; de paginering
            zou hem dan als enige inhoud naar een leeg vervolgvel schuiven. In de editor neemt
            hij dan ook geen ruimte in (de knoppen zweven), zodat de vellen gelijk blijven. */}
        {(isEditable || rateCard) && (
          <div
            contentEditable={false}
            className="investment-element-footer"
            style={rateCard ? undefined : { height: 0 }}
          >
            {isEditable ? (
              <InvestmentElementBar
                onAddOverview={handleAddOverview}
                onAddRateCard={startRateCard}
                onPickSaved={handleInsertFromLibrary}
                canAddRateCard={!rateCard}
              />
            ) : null}
          </div>
        )}

        {rateCard && (
          <div
            contentEditable={false}
            style={{ userSelect: 'none' }}
            className="investment-rate-card"
            data-sheet-unit
          >
            <div className="investment-rate-card-head">
              {canEditText ? (
                <>
                  <input
                    value={rateCardTitle}
                    onChange={(e) => setRateCard({ title: e.target.value, rows: rateCardRows })}
                    placeholder="Titel tarieventabel (b.v. Uurtarieven)"
                    className="investment-rate-card-title"
                    style={{
                      fontFamily: uboTheme.fonts.primaryFallback,
                      color: rateCardTitleColor,
                    }}
                  />
                  {isEditable && (
                    <button
                      type="button"
                      onClick={removeRateCard}
                      className="investment-rate-card-remove"
                      title="Tarieventabel verwijderen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Verwijderen
                    </button>
                  )}
                </>
              ) : (
                <h4
                  className="investment-rate-card-title"
                  style={{ fontFamily: uboTheme.fonts.primaryFallback, color: rateCardTitleColor }}
                >
                  {rateCardTitle}
                </h4>
              )}
            </div>

            <table className="investment-rate-card-table">
              <colgroup>
                <col />
                <col style={{ width: '32%' }} />
              </colgroup>
              <tbody>
                {rateCardRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      {canEditText ? (
                        <input
                          value={row.label}
                          onChange={(e) => updateRateRow(row.id, { label: e.target.value })}
                          placeholder={tableLabelsPreset.description}
                          className="investment-rate-card-cell"
                        />
                      ) : (
                        <span className="investment-rate-card-cell">{row.label}</span>
                      )}
                    </td>
                    <td>
                      {canEditText ? (
                        <>
                          <input
                            value={row.rateText}
                            onChange={(e) => updateRateRow(row.id, { rateText: e.target.value })}
                            placeholder={rateColumnLabel}
                            className="investment-rate-card-cell is-rate"
                          />
                          {isEditable && (
                            <button
                              type="button"
                              onClick={() => removeRateRow(row.id)}
                              className="investment-rate-card-row-remove"
                              title="Rij verwijderen"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="investment-rate-card-cell is-rate">{row.rateText}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {isEditable ? (
              <div className="investment-row-add" data-html2canvas-ignore="true">
                <button type="button" className="investment-row-add-action" onClick={addRateRow}>
                  <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  Regel toevoegen
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
}
